import { useState } from "react"
import { format } from "date-fns"
import {
	IconCreditCardMinusFill18 as CancelSubscriptionFill,
	IconCreditCardRefreshFill18 as ChangePlanFill,
} from "nucleo-ui-fill-18"
import {
	IconPercentageOutline18 as BadgePercent,
	IconCalendarClockOutline18 as CalendarClock,
	IconCreditCardMinusOutline18 as CancelSubscription,
	IconCreditCardRefreshOutline18 as ChangePlan,
	IconSquareArrowUpRightOutline18 as ExternalLink,
	IconMediaPlayOutline18 as Play,
} from "nucleo-ui-outline-18"

import {
	Button,
	Dialog,
	IconButton,
	Input,
	StripeIcon,
	Tooltip,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import {
	ADMIN_PLAN_LABELS,
	CancelSubscriptionDialog,
	ChangePlanDialog,
} from "./workspace-subscription-dialogs"

interface Props {
	workspaceId: string
	stripeCustomerId: string | null
	stripeSubscriptionId: string | null
	trialDaysLeft: number | null
}

const stripeUrl = (
	subscriptionId: string | null,
	customerId: string | null,
) => {
	if (subscriptionId) {
		return `https://dashboard.stripe.com/subscriptions/${subscriptionId}`
	}
	if (customerId) {
		return `https://dashboard.stripe.com/customers/${customerId}`
	}
	return null
}

export function WorkspaceStripeMenu({
	workspaceId,
	stripeCustomerId,
	stripeSubscriptionId,
	trialDaysLeft,
}: Props) {
	const utils = api.useUtils()
	const [extendOpen, setExtendOpen] = useState(false)
	const [cancelOpen, setCancelOpen] = useState(false)
	const [changePlanOpen, setChangePlanOpen] = useState(false)
	const [trialDaysInput, setTrialDaysInput] = useState(
		String(trialDaysLeft ?? 7),
	)

	const isTrialing = trialDaysLeft !== null && trialDaysLeft > 0
	const url = stripeUrl(stripeSubscriptionId, stripeCustomerId)

	const startTrial = api.analytics.startTrial.useMutation({
		onSuccess: async () => {
			toast.success({ title: "Trial started" })
			await utils.analytics.listWorkspaces.invalidate()
			await utils.analytics.listWorkspacesEnrichment.invalidate()
		},
		onError: (err) => {
			toast.error({ title: "Failed to start trial", description: err.message })
		},
	})

	const extendTrial = api.analytics.extendTrial.useMutation({
		onSuccess: async () => {
			toast.success({ title: "Trial extended" })
			setExtendOpen(false)
			await utils.analytics.listWorkspacesEnrichment.invalidate()
		},
		onError: (err) => {
			toast.error({ title: "Failed to extend trial", description: err.message })
		},
	})

	const applyStartupDiscount = api.analytics.applyStartupDiscount.useMutation({
		onSuccess: async () => {
			toast.success({ title: "Startup discount applied" })
			await utils.analytics.listWorkspaces.invalidate()
			await utils.analytics.listWorkspacesEnrichment.invalidate()
		},
		onError: (err) => {
			toast.error({
				title: "Failed to apply startup discount",
				description: err.message,
			})
		},
	})

	const refreshWorkspace = async () => {
		await utils.analytics.listWorkspaces.invalidate()
		await utils.analytics.listWorkspacesEnrichment.invalidate()
	}

	const cancelSubscription =
		api.analytics.cancelWorkspaceSubscription.useMutation({
			onSuccess: async (result) => {
				toast.success({
					title: result.immediately
						? "Subscription canceled"
						: `Subscription cancels on ${format(result.endsAt * 1000, "MMM d, yyyy")}`,
					icon: CancelSubscriptionFill,
				})
				setCancelOpen(false)
				await refreshWorkspace()
			},
			onError: (err) => {
				if (err.data?.code === "BAD_REQUEST") {
					toast.info({ title: err.message, icon: CancelSubscriptionFill })
					return
				}
				toast.error({
					title: "Stripe didn't cancel the subscription",
					description: err.message,
					icon: CancelSubscriptionFill,
				})
			},
		})

	const changePlan = api.analytics.changeWorkspacePlan.useMutation({
		onSuccess: async (_result, variables) => {
			toast.success({
				title: `Switched to ${ADMIN_PLAN_LABELS[variables.plan]}, billed ${variables.interval}`,
				icon: ChangePlanFill,
			})
			setChangePlanOpen(false)
			await refreshWorkspace()
		},
		onError: (err) => {
			if (err.data?.code === "BAD_REQUEST") {
				toast.info({ title: err.message, icon: ChangePlanFill })
				return
			}
			toast.error({
				title: "Stripe didn't change the plan",
				description: err.message,
				icon: ChangePlanFill,
			})
		},
	})

	return (
		<>
			<div className="flex items-center gap-1">
				{isTrialing ? (
					<Tooltip
						label={`${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left in trial`}
						shouldWrapChildren
					>
						<span className="rounded-[4px] bg-purple-50 px-[6px] py-[3px] text-xxs font-medium tabular-nums text-purple-700 dark:bg-purple-950 dark:text-purple-300">
							{trialDaysLeft}d
						</span>
					</Tooltip>
				) : null}
				<DropdownMenu
					trigger={
						<IconButton
							Icon={StripeIcon}
							variant="ghost"
							iconClassName="text-[#635BFF]"
						/>
					}
					contentProps={{ side: "bottom", align: "end" }}
				>
					<DropdownMenuGroup>
						{url ? (
							<DropdownMenuItem
								Icon={ExternalLink}
								onClick={() => {
									window.open(url, "_blank", "noopener,noreferrer")
								}}
							>
								Open in Stripe
							</DropdownMenuItem>
						) : null}
						{isTrialing ? (
							<DropdownMenuItem
								Icon={CalendarClock}
								onClick={() => {
									setTrialDaysInput(String(trialDaysLeft ?? 7))
									setExtendOpen(true)
								}}
							>
								Extend trial
							</DropdownMenuItem>
						) : (
							<DropdownMenuItem
								Icon={Play}
								disabled={startTrial.isLoading}
								onClick={() => {
									startTrial.mutate({ workspaceId })
								}}
							>
								Start new trial
							</DropdownMenuItem>
						)}
						{stripeSubscriptionId ? (
							<DropdownMenuItem
								Icon={BadgePercent}
								disabled={applyStartupDiscount.isLoading}
								onClick={() => {
									applyStartupDiscount.mutate({ workspaceId })
								}}
							>
								Add startup discount
							</DropdownMenuItem>
						) : null}
						{stripeSubscriptionId ? (
							<DropdownMenuItem
								Icon={ChangePlan}
								onClick={() => {
									setChangePlanOpen(true)
								}}
							>
								Change plan
							</DropdownMenuItem>
						) : null}
						{stripeSubscriptionId ? (
							<DropdownMenuItem
								Icon={CancelSubscription}
								onClick={() => {
									setCancelOpen(true)
								}}
							>
								Cancel subscription
							</DropdownMenuItem>
						) : null}
					</DropdownMenuGroup>
				</DropdownMenu>
			</div>

			<ChangePlanDialog
				isOpen={changePlanOpen}
				onOpenChange={setChangePlanOpen}
				isLoading={changePlan.isLoading}
				onConfirm={(input) => {
					changePlan.mutate({ workspaceId, ...input })
				}}
			/>

			<CancelSubscriptionDialog
				isOpen={cancelOpen}
				onOpenChange={setCancelOpen}
				isLoading={cancelSubscription.isLoading}
				onConfirm={(input) => {
					cancelSubscription.mutate({ workspaceId, ...input })
				}}
			/>

			<Dialog
				isOpen={extendOpen}
				onOpenChange={setExtendOpen}
				title="Extend trial"
				description="Set the new total number of trial days from now."
				footer={
					<>
						<Button
							variant="outline"
							onClick={() => {
								setExtendOpen(false)
							}}
						>
							Cancel
						</Button>
						<Button
							onClick={() => {
								const days = Number(trialDaysInput)
								if (!Number.isFinite(days) || days < 1 || days > 365) {
									toast.error({
										title: "Invalid trial length",
										description: "Enter a number between 1 and 365.",
									})
									return
								}
								extendTrial.mutate({
									workspaceId,
									trialDays: Math.floor(days),
								})
							}}
							disabled={extendTrial.isLoading}
						>
							Extend
						</Button>
					</>
				}
			>
				<div className="space-y-2 px-1 py-2">
					<label className="text-xs text-tertiary" htmlFor="trial-days-input">
						Trial days
					</label>
					<Input
						id="trial-days-input"
						name="trial-days-input"
						type="number"
						min={1}
						max={365}
						value={trialDaysInput}
						onChange={(e) => {
							setTrialDaysInput(e.target.value)
						}}
					/>
				</div>
			</Dialog>
		</>
	)
}
